import type { Coding, Coverage } from '@medplum/fhirtypes';

export const SUBSCRIBER_RELATIONSHIP_SYSTEM = 'http://terminology.hl7.org/CodeSystem/subscriber-relationship';

/** Who holds the policy, as intake asks it: from the patient's side. */
export type Policyholder = 'self' | 'spouse' | 'parent';

export const POLICYHOLDER_LABELS: Record<Policyholder, string> = {
  self: 'Myself',
  spouse: 'Spouse or partner',
  parent: 'Parent or guardian',
};

/**
 * `Coverage.relationship` is the beneficiary's relationship to the subscriber, so a parent
 * holding the policy makes the patient their `child`, not `parent`.
 */
const RELATIONSHIP_BY_POLICYHOLDER: Record<Policyholder, Coding> = {
  self: { system: SUBSCRIBER_RELATIONSHIP_SYSTEM, code: 'self', display: 'Self' },
  spouse: { system: SUBSCRIBER_RELATIONSHIP_SYSTEM, code: 'spouse', display: 'Spouse' },
  parent: { system: SUBSCRIBER_RELATIONSHIP_SYSTEM, code: 'child', display: 'Child' },
};

export function isPolicyholder(value: string | undefined): value is Policyholder {
  return value === 'self' || value === 'spouse' || value === 'parent';
}

export function subscriberRelationshipFor(policyholder: Policyholder): Coding {
  return RELATIONSHIP_BY_POLICYHOLDER[policyholder];
}

/**
 * Reads who holds the policy back from `Coverage.relationship`. Coverage written before
 * SLE-842 stored a parent policyholder as `parent`, which is read the same as `child`.
 */
export function policyholderFromCoverage(coverage: Coverage | undefined): Policyholder | undefined {
  switch (coverage?.relationship?.coding?.[0]?.code) {
    case 'self':
      return 'self';
    case 'spouse':
    case 'common':
      return 'spouse';
    case 'child':
    case 'parent':
      return 'parent';
    default:
      return undefined;
  }
}

export const INSURANCE_CARD_DOCUMENT_TYPE_SYSTEM = 'https://polarissleep.com/fhir/document-type';

export const INSURANCE_CARD_SIDES = {
  front: { code: 'insurance-card-front', title: 'Insurance card, front' },
  back: { code: 'insurance-card-back', title: 'Insurance card, back' },
} as const;

export type InsuranceCardSide = keyof typeof INSURANCE_CARD_SIDES;
